import React, {
    createContext,
    createRef,
    forwardRef,
    useContext,
    useEffect,
    useState,
} from 'react';
import {Button, IconButton, Grid} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import SettingsOverscanIcon from '@material-ui/icons/SettingsOverscan';
import {TransformWrapper, TransformComponent} from 'react-zoom-pan-pinch';
import SignedAsset from 'components/media/SignedAsset';
import {RotateLeft, RotateRight} from '@material-ui/icons';
import {StatusMessages} from 'components';
import {useGetRotateImage} from 'hooks/useGetEndpointActive';
import {isPdfFilename} from 'components/files/dropzone/helpers';
import Show from 'components/utils/Show';

const ButtonContext = createContext();

const ButtonContextProvider = ({children}) => {
    const [config, setConfig] = useState({
        back: {
            disabled: true,
            onClick: () => {},
        },
        next: {
            disabled: true,
            onClick: () => {},
        },
    });

    return (
        <ButtonContext.Provider
            value={{
                ...config,
                update: data =>
                    setConfig({
                        ...config,
                        ...data,
                    }),
            }}
        >
            {children}
        </ButtonContext.Provider>
    );
};

const DocumentViewerWrapper = forwardRef((props, ref) => {
    return (
        <ButtonContextProvider>
            <DocumentViewer {...props} ref={ref} />
        </ButtonContextProvider>
    );
});

const DocumentViewer = forwardRef(({assets = [], style}, ref) => {
    const transform = createRef();
    const {back, next, update} = useContext(ButtonContext);
    const [index, setIndex] = useState(0);
    const [totalRotate, setTotalRotate] = useState({});
    const {get, data, isLoading, error} = useGetRotateImage();

    const updateRotate = degs => {
        if (totalRotate?.[index]) {
            setTotalRotate({
                ...totalRotate,
                [index]: totalRotate?.[index] + degs,
            });
        } else {
            setTotalRotate({...totalRotate, [index]: degs});
        }
    };

    const rotateImage = () => {
        get(
            {
                rotation_degs: `${totalRotate?.[index] % 360}`,
            },
            assets[index].id
        );
    };

    useEffect(() => {
        if (data) {
            window.location.reload();
        }
        // eslint-disable-next-line
    }, [data]);

    useEffect(() => {
        update({
            back: {
                disabled: index === 0,
                onClick: e => {
                    setIndex(index - 1);
                },
            },
            next: {
                disabled: index === assets.length - 1,
                onClick: e => {
                    setIndex(index + 1);
                },
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assets, index]);

    const resetTransform = () => {
        transform.current?.resetTransform();
    }

    return assets.length ? (
        <div style={style}>
            <Show condition={!isPdfFilename(assets[index].document_filename)}>
                <TransformWrapper
                    pan={{velocity: false}}
                    options={{
                        limitToWrapper: true,
                    }}
                    ref={transform}
                >
                    <Grid item container style={{position: 'sticky', top: '4.5rem', alignSelf: 'flex-start'}}>
                        <Grid item container justifyContent='center' alignItems='center'>
                            <Grid item>
                                <Button
                                    {...back}
                                    startIcon={<ChevronLeftIcon />}
                                >
                                    Previous
                                </Button>
                            </Grid>
                            <Grid item>
                                <IconButton title='Reset Zoom' onClick={resetTransform}>
                                    <SettingsOverscanIcon />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <Button
                                    {...next}
                                    endIcon={<ChevronRightIcon />}
                                >
                                    Next
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            container
                            justifyContent='center'
                            alignItems='center'
                        >
                            <Grid item>
                                <IconButton
                                    onClick={() => updateRotate(-90)}
                                >
                                    <RotateLeft />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                {totalRotate?.[index] % 360 === 0 ||
                                totalRotate?.[index] === undefined ? (
                                    'Rotate'
                                ) : (
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        disabled={!get}
                                        onClick={rotateImage}
                                    >
                                        Save Rotation
                                    </Button>
                                )}
                            </Grid>
                            <Grid item>
                                <IconButton
                                    onClick={() => updateRotate(90)}
                                >
                                    <RotateRight />
                                </IconButton>
                            </Grid>
                        </Grid>
                        <TransformComponent
                            wrapperStyle={{
                                flexGrow: '1',
                            }}
                        >
                            <SignedAsset
                                asset={assets[index]}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 'calc(100vh - 12em)',
                                    transform: `rotate(${totalRotate?.[index]}deg)`,
                                    border: '1px solid black',
                                }}
                                ref={ref}
                                showControls
                            />
                        </TransformComponent>
                    </Grid>
                </TransformWrapper>
            </Show>
            <Show condition={isPdfFilename(assets[index].document_filename)}>
                <SignedAsset
                    asset={assets[index]}
                    style={{
                        maxWidth: '100%',
                        maxHeight: 'calc(100vh - 12em)',
                        transform: `rotate(${totalRotate?.[index]}deg)`,
                        border: '1px solid black',
                    }}
                    ref={ref}
                    showControls
                />
            </Show>
            <StatusMessages loading={isLoading} success={data} error={error} />
        </div>
    ) : null;
});

export default DocumentViewerWrapper;
export {ButtonContext};
